import { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import SocialNetworks from "./socialNetworks/SocialNetworks";
import { db } from "../firebase";
import api_key from "../env";
import default_org from "./logos/logoPaddedBlackBg.png";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import { getOrganization } from "../utility";
import ENV from "../env";
import Input from "./Input";
import "./EventDesc.scss";
import TicketItem from "./TicketItem";
import LoadingSpinner from "../components/LoadingSpinner";
import ClosedSpinner from "../components/ClosedSpinner";
import Dropdown from "./Dropdown";
import { createParticipant, createStripePayment } from "../utility";
import {
  buildOptionSelectionLabel,
  buildVariantKey,
  getOptionPriceDelta,
  getRemainingOptionStock,
} from "../utility/ticketOptions";
import "moment/locale/fr";
import "moment/locale/nl";
import dayjs from "dayjs";

const DEFAULT_OPTIONNAL_FIELDS = ["Phone number", "Age", "Address"];
const DEFAULT_MANDATORY_FIELDS = ["Firstname", "Name", "Email"];
const DEFAULT_FLOATING_TICKETS_GRADIENT =
  "linear-gradient(95deg, rgba(32, 215, 255, 0.58), rgba(139, 91, 255, 0.58), rgba(232, 75, 203, 0.58))";

const isValidHexColor = (color) => /^#[0-9a-f]{6}$/i.test(String(color || "").trim());
const hexToRgba = (color, alpha = 0.68) => {
  const normalizedColor = String(color || "").trim();
  if (!isValidHexColor(normalizedColor)) return null;

  const hex = normalizedColor.slice(1);
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const EventDesc = (props) => {
  const { t, i18n } = useTranslation(["eventdesc", "ticketitem"]);
  const [isThrottled, setIsThrottled] = useState(false);
  const isOpen = props.event?.open ?? false;
  const eventId = props.event?.id ?? "";
  const orgId = props.event?.orgId ?? "";
  const feeDelegated = props.event?.feeDelegated ?? false;
  const ticketSubdivision = props.event?.subdivision ?? false;
  const endDate = new Date(props.event?.endDate ?? new Date());
  const ansQues = props.event?.ansQues ?? [];
  const secretTicketId = props.secretTicketId;

  const [description, setDescription] = useState("");
  const [organization, setOrganization] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoadState, setTicketsLoadState] = useState("loading");
  const [showTicketsShortcut, setShowTicketsShortcut] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [fields, setFields] = useState([]);
  const [emailConfirmation, setEmailConfirmation] = useState("");
  const [emailConfirmationError, setEmailConfirmationError] = useState("");
  const [organizerContactAccepted, setOrganizerContactAccepted] = useState(false);
  const [organizerContactError, setOrganizerContactError] = useState(false);
  const organizerContactRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [enteredPromoCode, setEnteredPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoCodesLoaded, setPromoCodesLoaded] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [placeCoordinates, setPlaceCoordinates] = useState({
    lat: 50.850245,
    lng: 4.349689,
  });
  const [eventAddress, setEventAddress] = useState("");
  const [showPromoPrices, setShowPromoPrices] = useState(false);
  const [openOptionsTicketId, setOpenOptionsTicketId] = useState(null);
  const [optionDraftByTicket, setOptionDraftByTicket] = useState({});
  const [checkoutStep, setCheckoutStep] = useState("tickets");
  const formPanelRef = useRef(null);
  const formTopRef = useRef(null);
  const ticketsPanelRef = useRef(null);

  const OPTIONNAL_FIELDS_TRANSLATIONS = {
    "Phone number": { placeholder: t("phone_number_placeholder"), label: t("phone_number") },
    Age: { placeholder: t("age_placeholder"), label: t("age") },
    Address: { placeholder: t("address_placeholder"), label: t("address") },
  };

  const optionnalDefaultFields = props.event.formFields.filter((formField) =>
    DEFAULT_OPTIONNAL_FIELDS.includes(formField),
  );

  const customFormFields = props.event.formFields.filter(
    (formField) => !DEFAULT_OPTIONNAL_FIELDS.includes(formField),
  );

  const alert = useAlert();
  var moment = require("moment");
  moment.locale(i18n.language);

  const customFloatingTicketsGradient =
    isValidHexColor(props.event?.mainColor1) && isValidHexColor(props.event?.mainColor2)
      ? `linear-gradient(95deg, ${hexToRgba(props.event.mainColor1)}, ${hexToRgba(props.event.mainColor2)})`
      : DEFAULT_FLOATING_TICKETS_GRADIENT;

  const getTicketById = (ticketId, ticketsList = tickets) =>
    ticketsList.find((ticket) => ticket.id === ticketId);

  const getTicketAvailableQuantity = (ticket, promoCode = appliedPromoCode) => {
    const totalAvailable = Number(ticket?.numAvailable || 0);
    const sold = Number(ticket?.amountSold || 0);
    const allPromoReservations = Number(ticket?.reservedPromoQuantity || 0);
    const ownReservation = promoCode?.reserveTicketCapacity
      ? Number(promoCode.reservedTicketQuantities?.[ticket?.id] || 0)
      : 0;
    return Math.max(0, totalAvailable - sold - allPromoReservations - Number(ticket?.pendingCheckoutQuantity || 0) + ownReservation - Number(promoCode?.pendingPartnerReservations?.[ticket?.id] || 0));
  };

  const getPromoUnitPrice = (ticketId, fullUnitPrice, ticketsList = tickets, promoCode = appliedPromoCode) => {
    const ticket = getTicketById(ticketId, ticketsList);
    const ticketDiscount = promoCode?.ticketDiscounts?.find(
      (discount) => discount.ticketId === ticketId,
    );
    const discountValue = Number(
      ticketDiscount?.discountValue ?? ticketDiscount?.discountPercentage ?? ticket?.promoCodeDiscount ?? 0,
    );

    if (!Number.isFinite(discountValue) || discountValue <= 0) return fullUnitPrice;
    if (ticketDiscount?.isPercentage === false) return Math.max(0, fullUnitPrice - discountValue);
    return Math.max(0, fullUnitPrice * (1 - discountValue / 100));
  };

  const updateSelectedTickets = (updatedSelectedTickets, ticketsList = tickets, promoCode = appliedPromoCode) => {
    const recalculated = calculateTicketsPromo(updatedSelectedTickets, ticketsList, promoCode);
    const totalSum = recalculated.reduce((total, item) => total + item.sum, 0);
    setTotalAmount(Math.round(totalSum * 100) / 100);
    setSelectedTickets(recalculated);
  };

  const applyPromoCode = (promoCode) => {
    const updatedTickets = tickets.map((ticket) => {
      let promoPrice = ticket.price;

      const ticketDiscount = promoCode.ticketDiscounts?.find((td) => td.ticketId === ticket.id);

      if (ticketDiscount) {
        if (ticketDiscount.isPercentage === false) {
          promoPrice = Math.max(ticket.price - ticketDiscount.discountValue, 0);
        } else {
          const discountMultiplier =
            (100 - (ticketDiscount.discountValue ?? ticketDiscount.discountPercentage)) / 100;
          promoPrice = ticket.price * discountMultiplier;
        }
      } else if (ticket.promoCodeDiscount) {
        const discountMultiplier = (100 - parseFloat(ticket.promoCodeDiscount)) / 100;
        promoPrice = ticket.price * discountMultiplier;
      }

      promoPrice = Math.round(promoPrice * 100) / 100;

      if (promoPrice === ticket.price) return ticket;
      return { ...ticket, promoPrice };
    });

    setTickets(updatedTickets);
    updateSelectedTickets(selectedTickets, updatedTickets, promoCode);
  };

  const calculateTicketsPromo = (
    updatedSelectedTickets,
    ticketsList = tickets,
    promoCode = appliedPromoCode,
  ) => {
    const toRounded = (value) => Math.round(Number(value || 0) * 100) / 100;
    const getBaseUnitPrice = (selectedTicket) =>
      Number(selectedTicket.basePrice || 0) + Number(selectedTicket.optionPriceDelta || 0);
    const isPromoEligibleTicket = (selectedTicket) => {
      const ticket = ticketsList.find((t) => t.id === selectedTicket.ticketId);
      return ticket?.promoPrice || ticket?.promoPrice === 0;
    };

    if (!promoCode) {
      return updatedSelectedTickets.map((selectedTicket) => {
        const lineUnitPrice = getBaseUnitPrice(selectedTicket);
        return {
          ...selectedTicket,
          discountedUnits: 0,
          unitPrice: toRounded(lineUnitPrice),
          sum: toRounded(lineUnitPrice * Number(selectedTicket.number || 0)),
        };
      });
    }

    const remainingPromoUsage =
      promoCode.maxUsage === 0
        ? Number.POSITIVE_INFINITY
        : Math.max(0, Number(promoCode.maxUsage || 0) - Number(promoCode.currentUsage || 0));

    let remainingDiscountableUnits = remainingPromoUsage;

    if (promoCode.maxUsage !== 0) {
      const totalTicketsEligible = updatedSelectedTickets.reduce((total, selectedTicket) => {
        return isPromoEligibleTicket(selectedTicket)
          ? total + Number(selectedTicket.number || 0)
          : total;
      }, 0);

      setShowPromoPrices(totalTicketsEligible > 0 && remainingPromoUsage > 0);
    } else {
      setShowPromoPrices(true);
    }

    return updatedSelectedTickets.map((selectedTicket) => {
      const quantity = Number(selectedTicket.number || 0);
      const baseUnitPrice = getBaseUnitPrice(selectedTicket);
      const isEligible = isPromoEligibleTicket(selectedTicket);
      const discountedUnits = isEligible
        ? Math.min(quantity, Number.isFinite(remainingDiscountableUnits) ? remainingDiscountableUnits : quantity)
        : 0;

      if (isEligible && Number.isFinite(remainingDiscountableUnits)) {
        remainingDiscountableUnits -= discountedUnits;
      }

      const promoUnitPrice = getPromoUnitPrice(
        selectedTicket.ticketId,
        baseUnitPrice,
        ticketsList,
        promoCode,
      );
      const lineSum = promoUnitPrice * discountedUnits + baseUnitPrice * (quantity - discountedUnits);
      const effectiveUnitPrice = quantity > 0 ? lineSum / quantity : 0;

      return {
        ...selectedTicket,
        discountedUnits,
        unitPrice: toRounded(effectiveUnitPrice),
        sum: toRounded(lineSum),
      };
    });
  };

  const updateTicketQuantity = (newQuantity, ticket) => {
    const baseVariantKey = buildVariantKey(ticket.id, []);
    const ticketIndex = selectedTickets.findIndex((item) => item.variantKey === baseVariantKey);
    const nextSelectedTickets = [...selectedTickets];

    if (newQuantity === 0) {
      if (ticketIndex >= 0) {
        nextSelectedTickets.splice(ticketIndex, 1);
      }
      updateSelectedTickets(nextSelectedTickets);
      return;
    }

    if (newQuantity > ticket.maxUnitPerSale) {
      alert.show(`${t("maximum_tickets", { max: ticket.maxUnitPerSale, ns: "ticketitem" })}`, {
        type: "info",
      });
      return;
    }

    if (newQuantity > getTicketAvailableQuantity(ticket)) {
      alert.show(
        `${ticket.name} ${t("tickets_left", { count: getTicketAvailableQuantity(ticket), ns: "ticketitem" })}`,
        {
          type: "info",
        },
      );
      return;
    }

    const ticketData = {
      ticketId: ticket.id,
      baseTicketId: ticket.id,
      variantKey: baseVariantKey,
      name: ticket.name,
      number: newQuantity,
      basePrice: Number(ticket.price || 0),
      optionPriceDelta: 0,
      optionSelections: [],
      unitPrice: Number(ticket.price || 0),
      sum: Number(ticket.price || 0) * newQuantity,
      description: ticket.description,
    };

    if (ticketIndex >= 0) {
      nextSelectedTickets[ticketIndex] = ticketData;
    } else {
      nextSelectedTickets.push(ticketData);
    }

    updateSelectedTickets(nextSelectedTickets);
  };

  const buildDefaultOptionSelections = (ticket) => {
    const defaultSelections = {};
    (ticket.options || []).forEach((group) => {
      if (group.selectionType === "single" && group.required && group.values?.length > 0) {
        defaultSelections[group.id] = [group.values[0].id];
      } else {
        defaultSelections[group.id] = [];
      }
    });
    return defaultSelections;
  };

  const openOptionsDropdown = (ticket) => {
    setOpenOptionsTicketId((prev) => (prev === ticket.id ? null : ticket.id));
    setOptionDraftByTicket((prev) => {
      if (prev[ticket.id]) return prev;
      return {
        ...prev,
        [ticket.id]: {
          selections: buildDefaultOptionSelections(ticket),
          quantity: 1,
        },
      };
    });
  };

  const buildVariantSelectionsPayload = (ticket, optionSelectionsState) => {
    const optionSelections = [];
    for (const group of ticket.options || []) {
      const selectedValueIds = optionSelectionsState[group.id] || [];
      if (group.required && selectedValueIds.length === 0) {
        return { error: "required" };
      }

      for (const selectedValueId of selectedValueIds) {
        const selectedValue = (group.values || []).find((value) => value.id === selectedValueId);
        if (!selectedValue) continue;

        optionSelections.push({
          groupId: group.id,
          groupName: group.name,
          valueId: selectedValue.id,
          valueLabel: selectedValue.label,
          priceDelta: Number(selectedValue.priceDelta || 0),
        });
      }
    }
    return { optionSelections };
  };

  const getSelectionsForOptionValue = (ticket, targetGroupId, targetValueId) => {
    const draftSelections =
      optionDraftByTicket[ticket.id]?.selections || buildDefaultOptionSelections(ticket);
    const nextSelections = { ...draftSelections };

    for (const group of ticket.options || []) {
      const current = nextSelections[group.id] || [];
      if (group.id === targetGroupId) {
        nextSelections[group.id] = [targetValueId];
      } else if (group.selectionType === "single" && group.required && current.length === 0) {
        nextSelections[group.id] = group.values?.length ? [group.values[0].id] : [];
      } else {
        nextSelections[group.id] = current;
      }
    }

    return nextSelections;
  };

  const getVariantQuantityFromSelections = (ticket, optionSelectionsState) => {
    const { optionSelections } = buildVariantSelectionsPayload(ticket, optionSelectionsState);
    if (!optionSelections) return 0;
    const variantKey = buildVariantKey(ticket.id, optionSelections);
    const line = selectedTickets.find((item) => item.variantKey === variantKey);
    return Number(line?.number || 0);
  };

  const addOptionVariantToCart = (ticket, overrideSelections = null, overrideQuantity = null, silent = false) => {
    if (!ticket) return;
    const draft = optionDraftByTicket[ticket.id] || {
      selections: buildDefaultOptionSelections(ticket),
      quantity: 1,
    };
    const optionSelectionsState = overrideSelections || draft.selections || {};
    const optionQuantity = Math.max(1, Number(overrideQuantity ?? draft.quantity ?? 1));

    if (optionQuantity > ticket.maxUnitPerSale) {
      alert.show(
        `${t("maximum_tickets", { max: ticket.maxUnitPerSale, ns: "ticketitem" })}`,
        { type: "info" },
      );
      return;
    }

    const { optionSelections, error } = buildVariantSelectionsPayload(ticket, optionSelectionsState);
    if (error === "required") {
      if (!silent) alert.show(t("requiredOptionMissing"), { type: "error" });
      return;
    }

    const variantKey = buildVariantKey(ticket.id, optionSelections);
    const inCartForTicket = selectedTickets
      .filter((line) => line.ticketId === ticket.id)
      .reduce((sum, line) => sum + Number(line.number || 0), 0);
    const existingIndex = selectedTickets.findIndex((line) => line.variantKey === variantKey);
    const existingQuantity = existingIndex >= 0 ? Number(selectedTickets[existingIndex].number || 0) : 0;

    for (const selection of optionSelections) {
      const remainingStock = getRemainingOptionStock(
        ticket,
        selection.groupId,
        selection.valueId,
        selectedTickets,
      );
      if (remainingStock !== null && remainingStock + existingQuantity < optionQuantity) {
        alert.show(t("optionOutOfStock", { value: selection.valueLabel }), { type: "error" });
        return;
      }
    }

    if (optionQuantity + inCartForTicket - existingQuantity > getTicketAvailableQuantity(ticket)) {
      alert.show(
        `${ticket.name} ${t("tickets_left", {
          count: getTicketAvailableQuantity(ticket),
          ns: "ticketitem",
        })}`,
        { type: "info" },
      );
      return;
    }

    const optionLabel = buildOptionSelectionLabel(optionSelections);
    const optionPriceDelta = getOptionPriceDelta(optionSelections);
    const lineName = optionLabel ? `${ticket.name} (${optionLabel})` : ticket.name;

    const nextSelectedTickets = [...selectedTickets];
    const nextLine = {
      ticketId: ticket.id,
      baseTicketId: ticket.id,
      variantKey,
      name: lineName,
      number: optionQuantity,
      basePrice: Number(ticket.price || 0),
      optionPriceDelta: Number(optionPriceDelta || 0),
      optionSelections,
      unitPrice: Number(ticket.price || 0) + Number(optionPriceDelta || 0),
      sum: (Number(ticket.price || 0) + Number(optionPriceDelta || 0)) * optionQuantity,
      description: ticket.description,
    };

    if (existingIndex >= 0) {
      nextSelectedTickets[existingIndex] = nextLine;
    } else {
      nextSelectedTickets.push(nextLine);
    }

    updateSelectedTickets(nextSelectedTickets);
    if (!silent) {
      alert.show(
        t("optionAddedToCart", {
          count: optionQuantity,
          ticket: ticket.name,
        }),
        { type: "success" },
      );
    }
    setOptionDraftByTicket((prev) => ({
      ...prev,
      [ticket.id]: {
        ...(prev[ticket.id] || {}),
        quantity: 1,
      },
    }));
  };

  const setOptionValueQuantity = (ticket, group, value, nextQuantity) => {
    const safeQuantity = Math.max(0, Number(nextQuantity || 0));
    const nextSelections = getSelectionsForOptionValue(ticket, group.id, value.id);
    const { optionSelections } = buildVariantSelectionsPayload(ticket, nextSelections);
    if (!optionSelections) return;

    const variantKey = buildVariantKey(ticket.id, optionSelections);
    if (safeQuantity === 0) {
      const nextSelected = selectedTickets.filter((line) => line.variantKey !== variantKey);
      updateSelectedTickets(nextSelected);
      return;
    }

    setOptionDraftByTicket((prev) => ({
      ...prev,
      [ticket.id]: {
        ...(prev[ticket.id] || {}),
        selections: nextSelections,
        quantity: safeQuantity,
      },
    }));

    addOptionVariantToCart(ticket, nextSelections, safeQuantity, true);
  };

  const decrement = (ticket) => {
    const baseVariantKey = buildVariantKey(ticket.id, []);
    const currentTicket = selectedTickets.find((item) => item.variantKey === baseVariantKey);
    const currentQuantity = currentTicket?.number || 0;
    if (currentQuantity > 0) updateTicketQuantity(currentQuantity - 1, ticket);
  };

  const increment = (ticket) => {
    const baseVariantKey = buildVariantKey(ticket.id, []);
    const currentTicket = selectedTickets.find((item) => item.variantKey === baseVariantKey);
    const currentQuantity = currentTicket?.number || 0;
    updateTicketQuantity(currentQuantity + 1, ticket);
  };

  const updateQuantity = (event, ticket) => {
    const newQuantity = parseInt(event.target.value) || 0;
    if (newQuantity >= 0) updateTicketQuantity(newQuantity, ticket);
  };

  const saleEnded = moment(endDate).isBefore();

  const getTicketsToShow = (loadedTickets) => {
    const today = dayjs().startOf("day");

    return loadedTickets
      .filter((ticket) => {
        const passesSecretFilter = secretTicketId ? ticket.id === secretTicketId : !ticket.secret;

        let passesAvailabilityFilter = true;
        if (ticket.availableFrom && ticket.availableTo) {
          const fromDate = dayjs(ticket.availableFrom).startOf("day");
          const toDate = dayjs(ticket.availableTo).endOf("day");
          passesAvailabilityFilter = !today.isBefore(fromDate) && !today.isAfter(toDate);
        }

        return passesSecretFilter && passesAvailabilityFilter;
      })
      .map((ticket, index) => ({
        ...ticket,
        order: ticket.order ?? index,
      }))
      .sort((a, b) => a.order - b.order);
  };

  const loadTickets = async () => {
    setTicketsLoadState("loading");
    const ticketsRef = collection(db, "Events", eventId, "Tickets");
    try {
      const snapshot = await getDocs(ticketsRef);
      const loadedTickets = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const ticketsToShow = getTicketsToShow(loadedTickets);

      setTickets(ticketsToShow);
      setTicketsLoadState("ready");
    } catch (err) {
      console.error(t("fetch_tickets_failed"), err);
      setTickets([]);
      setTicketsLoadState("error");
    }
  };

  const loadPromoCodes = async (isCurrent = () => true) => {
    const promoCodesRef = collection(db, "Events", eventId, "PromoCodes");

    try {
      const snapshot = await getDocs(promoCodesRef);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const activePromoCodes = data.filter((promo) => promo.isActive === true);
      if (isCurrent()) {
        setPromoCodes(activePromoCodes);
        setPromoCodesLoaded(true);
      }
      return activePromoCodes;
    } catch (err) {
      console.log("Failed to fetch promo codes:", err);
      if (isCurrent()) setPromoCodesLoaded(false);
      return [];
    }
  };
  const inputChangeHandler = (changeEvent) => {
    const { id, value } = changeEvent.target;
    setFields((current) => {
      const field = { name: id, value };
      return current.some((item) => item.name === id)
        ? current.map((item) => item.name === id ? field : item)
        : [...current, field];
    });
    if (id === "Email") setEmailConfirmationError("");
  };

  const renderInput = (field, required = false, placeholder, label) => (
    <div className="col-12">
      <Input
        placeholder={placeholder ?? t("add_field", { field })}
        label={label ?? field}
        required={required}
        type="text"
        id={field}
        onChange={(e) => inputChangeHandler(e)}
      />
    </div>
  );

  const nbTicketUse = () => {
    if (appliedPromoCode && showPromoPrices) {
      let nbUsages = 0;
      selectedTickets.forEach((ticket) => {
        const discount = appliedPromoCode?.ticketDiscounts.find(
          (td) => td.ticketId === ticket.ticketId,
        );
        if (discount) nbUsages += Number(ticket.discountedUnits ?? ticket.number ?? 0);
      });
      return nbUsages;
    }
    return 0;
  };

  const fieldsValidation = () => {
    const allMandatoryFields = [
      ...DEFAULT_MANDATORY_FIELDS,
      ...customFormFields
        .filter((field) => field.isMandatory ?? true)
        .map((field) => field.value ?? field),
    ];

    return allMandatoryFields.every((mandatoryField) =>
      fields.some((field) => field.name === mandatoryField && field.value.trim() !== ""),
    );
  };

  const checkoutHandler = async (paymentMethod) => {
    const ticketsNumber = selectedTickets.reduce(
      (total, ticket) => (total += ticket.number ?? 0),
      0,
    );

    if (ticketsNumber === 0) {
      alert.show(t("select_at_least_one_ticket"), {
        type: "error",
      });
      return;
    }
    if (!fieldsValidation()) {
      alert.show(t("fill_all_form_fields"), {
        type: "error",
      });
      return;
    }

    const email = fields.find((field) => field.name === "Email")?.value.trim() || "";
    const confirmation = emailConfirmation.trim();
    if (!confirmation || email.toLowerCase() !== confirmation.toLowerCase()) {
      setEmailConfirmationError(!confirmation ? "email_confirmation_required" : "email_confirmation_mismatch");
      formPanelRef.current?.querySelector("#EmailConfirmation")?.focus();
      return;
    }

    if (!organizerContactAccepted) {
      setOrganizerContactError(true);
      organizerContactRef.current?.focus();
      return;
    }

    // Store the accepted wording and date alongside the existing participant details.
    const participantDetails = [
      ...fields,
      {
        name: "OrganizerContactConsent",
        value: "accepted",
        acceptedAt: new Date().toISOString(),
        text: t("organizer_contact_consent"),
        language: i18n.language,
      },
    ];
    setIsLoading(true);
    const participantField = fields.find((element) => element.name === "Email");
    const participantMail = participantField.value.trim();
    const participantPhoneNumber = fields.find((element) => element.name === "Phone Number");
    if (!participantMail.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      alert.show(t("invalid_email"), { type: "error" });
    } else if (participantPhoneNumber && !participantPhoneNumber.value.startsWith("+")) {
      alert.show(t("enter_phone_with_prefix"), { type: "error" });
    } else {
      const promoCodeUsageIncrement = appliedPromoCode ? nbTicketUse() || 0 : 0;
      const promoCodeId = appliedPromoCode?.id || null;

      if (totalAmount === 0) {
        await createParticipant(
          eventId,
          selectedTickets,
          participantDetails,
          ticketSubdivision,
          true,
          promoCodeId,
          promoCodeUsageIncrement,
        )
          .then(async (participantId) => {
            alert.show(t("registration_successful"), { type: "success" });
            window.open(
              `${ENV.hostname}/checkout/${eventId}/${participantId}/success`,
              "_self",
              "noreferrer",
            );
          })
          .catch((err) => {
            alert.show(t(({ TICKET_EMAIL_LIMIT: "ticket_email_limit", TICKET_SALE_LIMIT: "ticket_sale_limit", TICKET_EMAIL_REQUIRED: "ticket_email_required", PUBLIC_STOCK_UNAVAILABLE: "checkout_stock_unavailable", PUBLIC_PROMO_UNAVAILABLE: "checkout_promo_unavailable" })[err.code] || "registration_failed", { max: err.max, ticket: err.ticketName, items: (err.items || []).map(item => [item.name, item.option].filter(Boolean).join(" — ")).filter(Boolean).join(", ") }), {
              type: "error",
            });
            console.log(t("failed_to_save_participant") + err);
          });
      } else {
        await createStripePayment(
          totalAmount,
          selectedTickets,
          participantDetails,
          participantMail,
          orgId,
          eventId,
          paymentMethod,
          feeDelegated,
          ticketSubdivision,
          promoCodeId,
          promoCodeUsageIncrement,
        )
          .then(async () => {
            alert.show(t("payment_successful"), { type: "success" });
          })
          .catch((err) => {
            alert.show(t(({ TICKET_EMAIL_LIMIT: "ticket_email_limit", TICKET_SALE_LIMIT: "ticket_sale_limit", TICKET_EMAIL_REQUIRED: "ticket_email_required", PUBLIC_STOCK_UNAVAILABLE: "checkout_stock_unavailable", PUBLIC_PROMO_UNAVAILABLE: "checkout_promo_unavailable" })[err.code] || "payment_failed", { max: err.max, ticket: err.ticketName, items: (err.items || []).map(item => [item.name, item.option].filter(Boolean).join(" — ")).filter(Boolean).join(", ") }), { type: "error" });
            console.log(t("failed_to_save_participant") + err);
          });
      }
    }

    setIsLoading(false);
  };

  const goToFormStep = () => {
    const ticketsNumber = selectedTickets.reduce((total, ticket) => total + (ticket.number ?? 0), 0);
    if (ticketsNumber === 0) {
      alert.show(t("select_at_least_one_ticket"), { type: "error" });
      return;
    }
    setCheckoutStep("form");
  };

  const showOnMap = () => {
    window.open(
      `https://maps.google.com?q=${placeCoordinates.lat},${placeCoordinates.lng}`,
      "_blank",
    );
  };

  const getTotalQuantityForTicket = (ticketId) =>
    selectedTickets
      .filter((ticket) => ticket.ticketId === ticketId)
      .reduce((total, ticket) => total + Number(ticket.number || 0), 0);

  const updateCartLineQuantity = (variantKey, nextQuantity) => {
    const line = selectedTickets.find((item) => item.variantKey === variantKey);
    if (!line) return;

    const ticket = getTicketById(line.ticketId);
    if (!ticket) return;

    if (nextQuantity <= 0) {
      updateSelectedTickets(selectedTickets.filter((item) => item.variantKey !== variantKey));
      return;
    }

    if (nextQuantity > ticket.maxUnitPerSale) {
      alert.show(`${t("maximum_tickets", { max: ticket.maxUnitPerSale, ns: "ticketitem" })}`, {
        type: "info",
      });
      return;
    }

    const otherLinesQuantity = selectedTickets
      .filter((item) => item.ticketId === line.ticketId && item.variantKey !== variantKey)
      .reduce((sum, item) => sum + Number(item.number || 0), 0);

    if (otherLinesQuantity + nextQuantity > getTicketAvailableQuantity(ticket)) {
      alert.show(
        `${ticket.name} ${t("tickets_left", { count: getTicketAvailableQuantity(ticket), ns: "ticketitem" })}`,
        { type: "info" },
      );
      return;
    }

    if (Array.isArray(line.optionSelections)) {
      for (const selection of line.optionSelections) {
        const remainingStock = getRemainingOptionStock(
          ticket,
          selection.groupId,
          selection.valueId,
          selectedTickets,
        );
        if (remainingStock !== null && remainingStock + Number(line.number || 0) - nextQuantity < 0) {
          alert.show(t("optionOutOfStock", { value: selection.valueLabel }), { type: "error" });
          return;
        }
      }
    }

    const nextTickets = selectedTickets.map((item) =>
      item.variantKey === variantKey
        ? {
            ...item,
            number: nextQuantity,
          }
        : item,
    );
    updateSelectedTickets(nextTickets);
  };

  const addPromo = async () => {
    if (!enteredPromoCode) {
      return;
    }
    if (isThrottled) {
      alert.show(t("please_wait"), { type: "warning" });
      return;
    }

    setIsThrottled(true);
    setTimeout(() => setIsThrottled(false), 1000);

    if (promoCodeApplied) {
      alert.show(t("promo_code_already_active"), {
        type: "warning",
      });
      return;
    }

    const availablePromoCodes = promoCodesLoaded ? promoCodes : await loadPromoCodes();
    const foundPromoCode = availablePromoCodes.find(
      (promo) => promo.code.toUpperCase() === enteredPromoCode.toUpperCase() && promo.isActive,
    );

    if (foundPromoCode) {
      if (foundPromoCode.maxUsage !== 0 && foundPromoCode.currentUsage >= foundPromoCode.maxUsage) {
        alert.show(t("promo_code_exhausted"), {
          type: "error",
        });
        return;
      }

      const applicableTickets = selectedTickets.filter((selectedTicket) =>
        foundPromoCode.ticketDiscounts?.some((td) => td.ticketId === selectedTicket.ticketId),
      );

      if (applicableTickets.length === 0) {
        alert.show(t("promo_code_not_applicable"), {
          type: "error",
        });
        return;
      }

      setAppliedPromoCode(foundPromoCode);
      setPromoCodeApplied(enteredPromoCode);
      applyPromoCode(foundPromoCode);

      alert.success(t("promo_code_applied"), {
        type: "success",
      });
      return;
    }

    const matchingTickets = tickets.filter((ticket) => ticket.promoCodeName === enteredPromoCode);

    if (matchingTickets.length === 0) {
      alert.show(t("promo_code_not_exist"), {
        type: "error",
      });
      setPromoCodeApplied(false);
      return;
    }

    setAppliedPromoCode(null);
    setPromoCodeApplied(enteredPromoCode);

    alert.success(t("promo_code_applied"), {
      type: "success",
    });
  };

  const organizationData = async (orgId) => {
    const orgData = await getOrganization(orgId);
    setOrganization(orgData);
  };

  useEffect(() => {
    let current = true;
    if (props.event) {
      if (props.initialTicketsLoadState === "error") {
        setTickets([]);
        setTicketsLoadState("error");
      } else if (Array.isArray(props.initialTickets)) {
        setTickets(getTicketsToShow(props.initialTickets));
        setTicketsLoadState("ready");
      } else {
        loadTickets();
      }
      setPromoCodes([]);
      setPromoCodesLoaded(false);
      setEnteredPromoCode("");
      setAppliedPromoCode(null);
      setPromoCodeApplied(false);
      setOrganizerContactAccepted(false);
      setOrganizerContactError(false);
      if (eventId) loadPromoCodes(() => current);
      setCheckoutStep("tickets");
      if (props?.event?.eventLocation) {
        setPlaceCoordinates({
          lat: props.event.eventLocation.latitude,
          lng: props.event.eventLocation.longitude,
        });
      }
      if (props?.event?.eventAddress) {
        setEventAddress(props.event.eventAddress);
      }

      organizationData(orgId);
    }
    return () => {
      current = false;
    };
  }, [eventId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const updateShortcutVisibility = () => {
      const ticketsPanel = ticketsPanelRef.current;

      if (!mediaQuery.matches || !ticketsPanel || checkoutStep !== "tickets") {
        setShowTicketsShortcut(false);
        return;
      }

      const rect = ticketsPanel.getBoundingClientRect();
      const headerEl = document.querySelector("header");
      const headerOffset = headerEl?.offsetHeight || 88;
      const activationOffset = headerOffset + 20;
      const ticketsSectionIsBelowViewport = rect.top > window.innerHeight - 80;
      const userIsAboveTicketsSection = rect.top > activationOffset;
      const userHasPassedTicketsSection = rect.bottom <= activationOffset;

      setShowTicketsShortcut(
        (ticketsSectionIsBelowViewport || userIsAboveTicketsSection) && !userHasPassedTicketsSection,
      );
    };

    updateShortcutVisibility();
    window.addEventListener("scroll", updateShortcutVisibility, { passive: true });
    window.addEventListener("resize", updateShortcutVisibility);
    mediaQuery.addEventListener?.("change", updateShortcutVisibility);

    return () => {
      window.removeEventListener("scroll", updateShortcutVisibility);
      window.removeEventListener("resize", updateShortcutVisibility);
      mediaQuery.removeEventListener?.("change", updateShortcutVisibility);
    };
  }, [eventId, ticketsLoadState, checkoutStep]);

  const scrollToTickets = () => {
    ticketsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (props.event.descriptions) {
      const descriptions = props.event.descriptions;
      const newDescription =
        descriptions[i18n.language] || descriptions[Object.keys(descriptions)[0]];
      setDescription(newDescription);
    } else {
      setDescription(props.event.description);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (checkoutStep === "form" && selectedTickets.length === 0) {
      setCheckoutStep("tickets");
    }
  }, [checkoutStep, selectedTickets.length]);

  useEffect(() => {
    if (checkoutStep !== "form") return;

    const scrollToFormTop = () => {
      const targetEl = formTopRef.current || formPanelRef.current;
      if (!targetEl) return;

      const headerEl = document.querySelector("header");
      const headerOffset = headerEl?.offsetHeight || 88;
      const targetY = Math.max(
        0,
        targetEl.getBoundingClientRect().top + window.scrollY - headerOffset - 12,
      );

      window.scrollTo({
        top: targetY,
        behavior: "smooth",
      });
    };

    const rafId = window.requestAnimationFrame(scrollToFormTop);
    const timeoutId = window.setTimeout(scrollToFormTop, 320);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [checkoutStep]);

  return (
    <section className="event-desc checkout-public">
      <div className="container">
        {(!isOpen || saleEnded) && (
          <div className="section-header">
            <h2 className="title">{t("sale_closed")}</h2>
            <ClosedSpinner height="50%" width="50%" />
            <div className="col-12">
              <button
                type="submit"
                className="submitBtn"
                onClick={() => {
                  window.open(`https://comitty.eu`, "_parent", "noreferrer");
                }}
              >
                {t("return_to_comitty")}
              </button>
            </div>
          </div>
        )}

        {isOpen && !saleEnded && (
          <div className="__details-wrapper">
            <div className="__details-wrapper-left">
              {props.event?.name && (
                <div className="event-sale-title-block">
                  <h1 className="event-sale-title">{props.event.name}</h1>
                </div>
              )}
              {props.isEmbedded && (
                <button
                  type="button"
                  className="embeddedTicketsShortcut"
                  style={{ "--floating-tickets-gradient": customFloatingTicketsGradient }}
                  onClick={scrollToTickets}
                >
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 5V19M12 19L6 13M12 19L18 13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t("choose_your_tickets")}
                </button>
              )}
              <div className="__details-wrapper-left-description">
                <h3 className="__details-wrapper-left-description-title">{t("description")}</h3>
                <p className="event-sale-description">{description}</p>
              </div>
              {props.event?.eventFromDate && props.event?.eventToDate && (
                <div className="__ticket-calendar checkout-info-section">
                  <h3 className="__ticket-calendar-title">{t("calendar")}</h3>
                  <div className="__ticket-calendar-items">
                    <div className="__ticket-date">
                      <div className="__ticket-date-icon">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 40 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M28.334 4.99935H35.0007C35.4427 4.99935 35.8666 5.17494 36.1792 5.4875C36.4917 5.80006 36.6673 6.22399 36.6673 6.66602V33.3327C36.6673 33.7747 36.4917 34.1986 36.1792 34.5112C35.8666 34.8238 35.4427 34.9993 35.0007 34.9993H5.00065C4.55862 34.9993 4.1347 34.8238 3.82214 34.5112C3.50958 34.1986 3.33398 33.7747 3.33398 33.3327V6.66602C3.33398 6.22399 3.50958 5.80006 3.82214 5.4875C4.1347 5.17494 4.55862 4.99935 5.00065 4.99935H11.6673V1.66602H15.0007V4.99935H25.0007V1.66602H28.334V4.99935ZM6.66732 14.9993V31.666H33.334V14.9993H6.66732ZM10.0007 21.666H18.334V28.3327H10.0007V21.666Z"
                            fill="#8BA4BF"
                          />
                        </svg>
                      </div>
                      <div className="__ticket-date-cont">
                        <span className="info">{t("start")}</span>
                        <span className="subtitle">
                          {moment(new Date(props.event?.eventFromDate)).format("DD MMM, HH:mm")}
                        </span>
                      </div>
                    </div>
                    <div className="__ticket-date">
                      <div className="__ticket-date-icon">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 40 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M28.334 4.99935H35.0007C35.4427 4.99935 35.8666 5.17494 36.1792 5.4875C36.4917 5.80006 36.6673 6.22399 36.6673 6.66602V33.3327C36.6673 33.7747 36.4917 34.1986 36.1792 34.5112C35.8666 34.8238 35.4427 34.9993 35.0007 34.9993H5.00065C4.55862 34.9993 4.1347 34.8238 3.82214 34.5112C3.50958 34.1986 3.33398 33.7747 3.33398 33.3327V6.66602C3.33398 6.22399 3.50958 5.80006 3.82214 5.4875C4.1347 5.17494 4.55862 4.99935 5.00065 4.99935H11.6673V1.66602H15.0007V4.99935H25.0007V1.66602H28.334V4.99935ZM6.66732 14.9993V31.666H33.334V14.9993H6.66732ZM10.0007 21.666H18.334V28.3327H10.0007V21.666Z"
                            fill="#8BA4BF"
                          />
                        </svg>
                      </div>
                      <div className="__ticket-date-cont">
                        <span className="info">{t("end")}</span>
                        <span className="subtitle">
                          {moment(new Date(props.event?.eventToDate)).format("DD MMM, HH:mm")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {eventAddress && placeCoordinates && (
                <div className="__localization-form checkout-info-section">
                  <h3 className="__localization-form-title">{t("localization")}</h3>
                  <p className="checkout-address">{eventAddress}</p>
                  <div className="checkout-location-actions">
                    <button type="button" className="checkout-text-action" onClick={showOnMap}>
                      {t("open_in_maps")} <span aria-hidden="true">↗</span>
                    </button>
                  </div>
                  <div id="checkout-location-map" className="checkout-location-map">
                    <iframe
                      title={t("location_map")}
                      loading="lazy"
                      width="100%"
                      height="220"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${placeCoordinates.lng - 0.01},${placeCoordinates.lat - 0.01},${placeCoordinates.lng + 0.01},${placeCoordinates.lat + 0.01}&layer=mapnik&marker=${placeCoordinates.lat},${placeCoordinates.lng}`}
                    />
                  </div>
                </div>
              )}

              {organization !== null && (
                <div className="__ticket-contact-details checkout-info-section">
                  <h3 className="__ticket-calendar-title">{t("contact")}</h3>
                  <div className="__ticket-contact-area">
                    <div className="__ticket-contact-area-container">
                      <div className="__ticket-contact-area-image">
                        <img
                          src={organization.org.logo || props?.event?.logoUrl || default_org}
                          alt={t("logo")}
                        />
                      </div>
                      <div className="__ticket-contact-area-title">
                        <div className="title">{organization.org.organization}</div>
                        <div className="email">{organization.org?.contactEmail}</div>
                      </div>
                    </div>
                    <div className="__ticket-contact-area-button">
                      {organization.org?.contactEmail ? (
                        <a
                          className="contactBtn"
                          style={{ textDecoration: "none" }}
                          href={`mailto:${organization.org.contactEmail}`}
                        >
                          {t("contact_organizer")}
                        </a>
                      ) : (
                        <button className="contactBtn" disabled>
                          {t("no_contact_available")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="checkout-info-section checkout-share-section">
                <SocialNetworks eventId={eventId} compact />
              </div>
            </div>

            <div className="__details-wrapper-right">
              <div className={`checkout-stage checkout-stage-${checkoutStep}`}>
                <div className="checkout-stage-panel checkout-tickets-panel" ref={ticketsPanelRef} hidden={checkoutStep !== "tickets"}>
                  {ticketsLoadState === "loading" ? (
                    <LoadingSpinner height="60%" width="60%" />
                  ) : ticketsLoadState === "error" ? (
                    <div className="ticket-load-state">
                      <p>{t("fetch_tickets_failed")}</p>
                      <button type="button" className="checkoutBtn" onClick={loadTickets}>
                        {t("retry")}
                      </button>
                    </div>
                  ) : tickets.length ? (
                    <div className="__ticket-wrapper">
                      <h2 className="__ticket-wrapper-title">{t("choose_your_tickets")}</h2>
                      {tickets.map((ticket) => (
                        <div key={ticket.id}>
                          <TicketItem
                            discount={props.event?.promoCodeDiscount}
                            ticket={ticket}
                            selectedTickets={selectedTickets}
                            increment={() => increment(ticket)}
                            decrement={() => decrement(ticket)}
                            updateQuantity={(e) => updateQuantity(e, ticket)}
                            onTicketClick={() => increment(ticket)}
                            quantityInCart={getTotalQuantityForTicket(ticket.id)}
                            availableQuantity={getTicketAvailableQuantity(ticket)}
                            hasOptions={Array.isArray(ticket.options) && ticket.options.length > 0}
                            onToggleOptions={() => openOptionsDropdown(ticket)}
                            isOptionsOpen={openOptionsTicketId === ticket.id}
                            showPromoPrice={showPromoPrices}
                          />
                          {Array.isArray(ticket.options) &&
                            ticket.options.length > 0 &&
                            openOptionsTicketId === ticket.id && (
                              <div className="ticket-options-dropdown">
                                {(ticket.options || []).map((group) => {
                                  return (
                                    <div key={group.id} className="ticket-options-group">
                                      <label>
                                        {group.name}
                                        {group.required ? " *" : ""}
                                      </label>
                                      <div className="ticket-options-values">
                                        {(group.values || []).map((value) => {
                                          const remainingStock = getRemainingOptionStock(
                                            ticket,
                                            group.id,
                                            value.id,
                                            selectedTickets,
                                          );
                                          const rowSelections = getSelectionsForOptionValue(
                                            ticket,
                                            group.id,
                                            value.id,
                                          );
                                          const quantity = getVariantQuantityFromSelections(
                                            ticket,
                                            rowSelections,
                                          );
                                          const isOutOfStock =
                                            remainingStock !== null && remainingStock <= 0 && quantity === 0;
                                          return (
                                            <div
                                              key={value.id}
                                              className={`__ticket-item ticket-option-ticket-item ${
                                                isOutOfStock ? "is-out-of-stock" : ""
                                              }`}
                                              onClick={() => {
                                                if (isOutOfStock) return;
                                                setOptionValueQuantity(ticket, group, value, quantity + 1);
                                              }}
                                              role="button"
                                              tabIndex={0}
                                              onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                  event.preventDefault();
                                                  if (isOutOfStock) return;
                                                  setOptionValueQuantity(ticket, group, value, quantity + 1);
                                                }
                                              }}
                                            >
                                              <div className="__ticket-item-icon">
                                                {ticket.image ? (
                                                  <img
                                                    src={ticket.image}
                                                    alt={value.label}
                                                    className="ticket-icon-image"
                                                  />
                                                ) : (
                                                  <svg
                                                    viewBox="0 0 40 41"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <g clipPath={`url(#clip-ticket-option-${ticket.id}-${value.id})`}>
                                                      <path
                                                        d="M35.0002 5.875C35.4422 5.875 35.8661 6.05059 36.1787 6.36316C36.4912 6.67572 36.6668 7.09964 36.6668 7.54167V16.7083C35.5618 16.7083 34.502 17.1473 33.7206 17.9287C32.9392 18.7101 32.5002 19.7699 32.5002 20.875C32.5002 21.9801 32.9392 23.0399 33.7206 23.8213C34.502 24.6027 35.5618 25.0417 36.6668 25.0417V34.2083C36.6668 34.6504 36.4912 35.0743 36.1787 35.3868C35.8661 35.6994 35.4422 35.875 35.0002 35.875H5.00016C4.55814 35.875 4.13421 35.6994 3.82165 35.3868C3.50909 35.0743 3.3335 34.6504 3.3335 34.2083V25.0417C4.43856 25.0417 5.49837 24.6027 6.27977 23.8213C7.06118 23.0399 7.50016 21.9801 7.50016 20.875C7.50016 19.7699 7.06118 18.7101 6.27977 17.9287C5.49837 17.1473 4.43856 16.7083 3.3335 16.7083V7.54167C3.3335 7.09964 3.50909 6.67572 3.82165 6.36316C4.13421 6.05059 4.55814 5.875 5.00016 5.875H35.0002ZM33.3335 9.20833H6.66683V14.155L6.92683 14.29C8.05736 14.9067 9.01004 15.8042 9.69311 16.8959C10.3762 17.9877 10.7666 19.2369 10.8268 20.5233L10.8335 20.875C10.8337 22.2221 10.471 23.5443 9.78358 24.7028C9.09614 25.8612 8.10931 26.8131 6.92683 27.4583L6.66683 27.595V32.5417H33.3335V27.5933L33.0735 27.46C31.943 26.8433 30.9903 25.9458 30.3072 24.8541C29.6242 23.7623 29.2337 22.5131 29.1735 21.2267L29.1668 20.875C29.1668 18.035 30.7452 15.5633 33.0735 14.2917L33.3335 14.1533V9.20833ZM26.6668 15.875V25.875H13.3335V15.875H26.6668Z"
                                                        fill="#8BA4BF"
                                                      />
                                                    </g>
                                                    <defs>
                                                      <clipPath id={`clip-ticket-option-${ticket.id}-${value.id}`}>
                                                        <rect
                                                          width="40"
                                                          height="40"
                                                          fill="white"
                                                          transform="translate(0 0.875)"
                                                        />
                                                      </clipPath>
                                                    </defs>
                                                  </svg>
                                                )}
                                              </div>
                                              <div className="__ticket-item-cont">
                                                <div>
                                                  <span className="info">
                                                    {quantity > 0
                                                      ? t("inCartCount", { count: quantity, ns: "ticketitem" })
                                                      : remainingStock !== null
                                                        ? t("optionStockRemainingShort", { count: remainingStock })
                                                        : t("unlimited")}
                                                  </span>
                                                  <h6 className="subtitle">{value.label}</h6>
                                                  <span className="price">
                                                    +{Number(value.priceDelta || 0).toFixed(2)} €
                                                  </span>
                                                </div>
                                                <div className="right-detail">
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    className="quantity"
                                                    value={quantity}
                                                    onChange={(event) =>
                                                      setOptionValueQuantity(
                                                        ticket,
                                                        group,
                                                        value,
                                                        parseInt(event.target.value, 10) || 0,
                                                      )
                                                    }
                                                    onClick={(event) => event.stopPropagation()}
                                                    disabled={isOutOfStock}
                                                  />
                                                  <div className="qty-btns">
                                                    <div
                                                      className="qty-btn"
                                                      onClick={(event) => {
                                                        event.stopPropagation();
                                                        setOptionValueQuantity(
                                                          ticket,
                                                          group,
                                                          value,
                                                          quantity + 1,
                                                        );
                                                      }}
                                                    >
                                                      <svg
                                                        className="qty-btn-icon"
                                                        width="26"
                                                        height="23"
                                                        viewBox="0 0 26 23"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                      >
                                                        <path d="M11.935 10.3828V4.80469H14.0647V10.3828H20.4536V12.2422H14.0647V17.8203H11.935V12.2422H5.54614V10.3828H11.935Z" />
                                                      </svg>
                                                    </div>
                                                    <div
                                                      className="qty-btn"
                                                      onClick={(event) => {
                                                        event.stopPropagation();
                                                        setOptionValueQuantity(
                                                          ticket,
                                                          group,
                                                          value,
                                                          Math.max(0, quantity - 1),
                                                        );
                                                      }}
                                                    >
                                                      <svg
                                                        className="qty-btn-icon"
                                                        width="26"
                                                        height="24"
                                                        viewBox="0 0 26 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                      >
                                                        <path d="M11.935 11.0078H14.0647H20.4536V12.8672H14.0647H11.935H5.54614V11.0078H11.935Z" />
                                                      </svg>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      ))}

                      {((promoCodesLoaded && promoCodes.length > 0) ||
                        tickets.some((ticket) => String(ticket.promoCodeName || "").trim())) && (
                        <div className="ticket-promo-container">
                          <div className="input-promo-container">
                            <Input
                              placeholder={t("promo_code")}
                              label={t("promo_code")}
                              type="text"
                              id="Promo_Code"
                              onChange={(e) => setEnteredPromoCode(e.target.value)}
                            />
                          </div>
                          <div className="apply-promo-container">
                            <button className="applyPromo" onClick={() => addPromo()}>
                              {t("add")}
                            </button>
                          </div>
                        </div>
                      )}

                      {appliedPromoCode && (
                        <p className="promo-code-info">
                          {appliedPromoCode.maxUsage === 0
                            ? t("promo_usage_unlimited")
                            : t("promo_usage_message", {
                                count: appliedPromoCode.maxUsage - appliedPromoCode.currentUsage,
                              })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="ticket-load-state">
                      <p>{t("no_tickets_available")}</p>
                    </div>
                  )}
                </div>

                <div className="checkout-stage-panel checkout-form-panel" ref={formPanelRef} hidden={checkoutStep !== "form"}>
                  <div className="checkout-form-top" ref={formTopRef}>
                    <button onClick={() => setCheckoutStep("tickets")} className="checkoutBackBtn">
                      <span aria-hidden="true">←</span> {t("backToTickets")}
                    </button>
                  </div>
                  <div className="__details-form">
                    <h3 className="__details-form-title">{t("your_details")}</h3>
                    <p className="__details-form-mandatory-fields"><span>*</span> {t("required_fields")}</p>
                    <div className="row g-2 g-lg-3">
                      <div className="col-12 col-sm-6">
                        <Input
                          placeholder={t("add_firstname")}
                          label={t("firstname")}
                          autoComplete="given-name"
                          type="text"
                          id="Firstname"
                          onChange={(e) => inputChangeHandler(e)}
                          required="required"
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <Input
                          placeholder={t("add_lastname")}
                          label={t("lastname")}
                          autoComplete="family-name"
                          type="text"
                          id="Name"
                          onChange={(e) => inputChangeHandler(e)}
                          required="required"
                        />
                      </div>
                      <div className="col-12">
                        <Input
                          placeholder={t("email_placeholder")}
                          label={t("email")}
                          autoComplete="email"
                          type="email"
                          id="Email"
                          inputMode="email"
                          autoCapitalize="none"
                          spellCheck={false}
                          onChange={(e) => inputChangeHandler(e)}
                          required="required"
                        />
                        <p className="checkout-email-hint">{t("ticket_email_hint")}</p>
                      </div>
                      <div className="col-12">
                        <Input
                          placeholder={t("email_confirmation_placeholder")}
                          label={t("email_confirmation")}
                          id="EmailConfirmation"
                          name="emailConfirmation"
                          type="email"
                          inputMode="email"
                          autoComplete="off"
                          autoCapitalize="none"
                          spellCheck={false}
                          required
                          value={emailConfirmation}
                          aria-invalid={Boolean(emailConfirmationError)}
                          aria-describedby={emailConfirmationError ? "email-confirmation-error" : undefined}
                          onChange={(event) => {
                            setEmailConfirmation(event.target.value);
                            setEmailConfirmationError("");
                          }}
                        />
                        {emailConfirmationError && (
                          <p id="email-confirmation-error" className="checkout-email-error" role="alert">
                            {t(emailConfirmationError)}
                          </p>
                        )}
                      </div>

                      {customFormFields.map((field) =>
                        renderInput(field.value ?? field, field.isMandatory ?? true),
                      )}
                      {optionnalDefaultFields.map((field) =>
                        renderInput(
                          field,
                          false,
                          OPTIONNAL_FIELDS_TRANSLATIONS[field].placeholder,
                          OPTIONNAL_FIELDS_TRANSLATIONS[field].label,
                        ),
                      )}

                      {ansQues?.length > 0 &&
                        ansQues.map((questionObj, questionIndex) => (
                          <div className="col-12 question-container" key={questionIndex}>
                            <Dropdown
                              options={questionObj.answers.map((answer) => ({
                                value: answer.text,
                                label: answer.text,
                              }))}
                              value={null}
                              label={questionObj.question}
                              onChange={(selectedOption) =>
                                inputChangeHandler({
                                  target: {
                                    id: questionObj.question,
                                    value: selectedOption.value,
                                  },
                                })
                              }
                            />
                          </div>
                        ))}

                      <div className="col-12 checkout-organizer-consent">
                        <label htmlFor="organizer-contact-consent">
                          <input
                            ref={organizerContactRef}
                            id="organizer-contact-consent"
                            type="checkbox"
                            required
                            checked={organizerContactAccepted}
                            aria-invalid={organizerContactError}
                            aria-describedby={organizerContactError ? "organizer-contact-error" : undefined}
                            onChange={(event) => {
                              setOrganizerContactAccepted(event.target.checked);
                              setOrganizerContactError(false);
                            }}
                          />
                          <span>{t("organizer_contact_consent")} <span aria-hidden="true">*</span></span>
                        </label>
                        {organizerContactError && (
                          <p id="organizer-contact-error" role="alert">
                            {t("organizer_contact_required")}
                          </p>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
              <div className="__ticket-checkout">
                {selectedTickets.length > 0 && (
                  <div className="ticket-cart-review">
                    <h4>{t("cartReview")}</h4>
                    {selectedTickets.map((line) => (
                      <div className="ticket-cart-row" key={line.variantKey || `${line.ticketId}-${line.name}`}>
                        <div className="ticket-cart-row-main">
                          <div className="ticket-cart-row-name">{line.name}</div>
                          <div className="ticket-cart-row-price">{Number(line.sum || 0).toFixed(2)} €</div>
                        </div>
                        <div className="ticket-cart-row-actions">
                          <button type="button" aria-label={t("decrease_ticket_quantity", { ticket: line.name })} onClick={() => updateCartLineQuantity(line.variantKey, Number(line.number || 0) - 1)}>
                            -
                          </button>
                          <span>{line.number}</span>
                          <button type="button" aria-label={t("increase_ticket_quantity", { ticket: line.name })} onClick={() => updateCartLineQuantity(line.variantKey, Number(line.number || 0) + 1)}>
                            +
                          </button>
                          <button onClick={() => updateCartLineQuantity(line.variantKey, 0)}>
                            {t("remove")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="__ticket-checkout-titles">
                  <div className="subtitle">{t("total_amount")}</div>
                  <h4 className="total-price">{totalAmount} €</h4>
                </div>
                {isLoading ? (
                  <LoadingSpinner height="80%" width="80%" />
                ) : (
                  <div className="__ticket-checkout-buttons">
                    {checkoutStep === "tickets" ? (
                      <button onClick={goToFormStep} className="checkoutBtn checkoutContinueBtn">
                        {t("continueToForm")}
                      </button>
                    ) : totalAmount > 0 ? (
                      <>
                        <button
                          onClick={() => checkoutHandler("bancontact")}
                          className="checkoutBtn"
                        >
                          {t("pay_with_bancontact")}
                        </button>
                        <button onClick={() => checkoutHandler("card")} className="checkoutBtn checkoutCardPaymentBtn">
                          {t("pay_with_mastercard_visa")}
                        </button>
                      </>
                    ) : (
                      <button onClick={() => checkoutHandler("bancontact")} className="checkoutBtn">
                        {t("confirm")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {!props.isEmbedded && showTicketsShortcut && (
              <button
                type="button"
                className="floatingTicketsButton"
                style={{ "--floating-tickets-gradient": customFloatingTicketsGradient }}
                onClick={scrollToTickets}
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 5V19M12 19L6 13M12 19L18 13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("choose_your_tickets")}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default EventDesc;
